import { useEffect, useState } from "react";
import api from "../api";
import DateIT from "../components/DateIT";

const DAYS = [
  { id: "lun", label: "Lunedì" },
  { id: "mar", label: "Martedì" },
  { id: "mer", label: "Mercoledì" },
  { id: "gio", label: "Giovedì" },
  { id: "ven", label: "Venerdì" },
  { id: "sab", label: "Sabato" },
  { id: "dom", label: "Domenica" },
];

const SmsPreview = ({ text, kind }) => {
  const tomorrow = new Date(Date.now() + 86400000);
  const data = `${String(tomorrow.getDate()).padStart(2, "0")}/${String(tomorrow.getMonth() + 1).padStart(2, "0")}`;
  const link = kind === "review"
    ? "https://g.page/r/…/review"
    : `${window.location.origin}/conferma?appt=…`;
  let t = text || "";
  if (kind === "confirm" && t && !t.includes("{link}")) t += "\nConfermi con un tocco qui: {link}";
  if (kind === "confirm" && t && !t.includes("{modalita}")) t += "\n{modalita}";
  if (kind === "reminder" && t && !t.includes("{modalita}")) t += " {modalita}";
  t = t.replaceAll("{nome}", "Silvia").replaceAll("{data}", data).replaceAll("{ora}", "10:30").replaceAll("{link}", link)
    .replaceAll("{modalita}", "Veniamo noi presso la vostra sede.");
  if (!t.trim()) return null;
  return (
    <div className="sms-preview" data-testid={`sms-preview-${kind}`}>
      <span className="sms-preview-label">👁 Anteprima (come la riceve il cliente):</span>
      <div className="sms-bubble">{t}</div>
    </div>
  );
};

export default function CalendarSettings() {
  const [ic, setIc] = useState({ apple_id: "", password: "" });
  const [icStatus, setIcStatus] = useState({ connected: false, calendars: [] });
  const [gcal, setGcal] = useState({ connected: false, calendars: [] });
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const loadIc = () => api.get("/icloud/status").then((r) => setIcStatus(r.data)).catch(() => {});
  const loadCfg = () => api.get("/booking-config").then((r) => setCfg(r.data)).catch(() => {});
  const loadGcal = () => {
    api.get("/calendar/status").then((r) => {
      if (r.data.connected) api.get("/calendar/calendars").then((c) => setGcal({ connected: true, calendars: c.data })).catch(() => setGcal({ connected: true, calendars: [] }));
      else setGcal({ connected: false, calendars: [] });
    }).catch(() => {});
  };
  useEffect(() => { loadIc(); loadCfg(); loadGcal(); }, []);

  const connectIc = async () => {
    if (!ic.apple_id || !ic.password) { alert("Inserisci Apple ID e password specifica per app"); return; }
    setBusy(true);
    try {
      const r = await api.post("/icloud/connect", ic);
      setNotice(`iCloud collegato: ${r.data.calendars?.length || 0} calendari trovati.`);
      setIc({ apple_id: "", password: "" }); loadIc();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const disconnectIc = async () => {
    if (!window.confirm("Scollegare iCloud Calendar?")) return;
    await api.delete("/icloud/disconnect"); loadIc();
  };

  // --- orari + chiusure ---
  const addSlot = (day) => {
    const hours = { ...(cfg.hours || {}) };
    hours[day] = [...(hours[day] || []), { from: "09:00", to: "13:00" }];
    setCfg({ ...cfg, hours });
  };
  const updSlot = (day, i, field, val) => {
    const hours = { ...(cfg.hours || {}) };
    hours[day] = (hours[day] || []).map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    setCfg({ ...cfg, hours });
  };
  const delSlot = (day, i) => {
    const hours = { ...(cfg.hours || {}) };
    hours[day] = (hours[day] || []).filter((_, idx) => idx !== i);
    setCfg({ ...cfg, hours });
  };
  const addClosure = () => setCfg({ ...cfg, closures: [...(cfg.closures || []), { from: "", to: "", reason: "" }] });
  const updClosure = (i, field, val) => setCfg({ ...cfg, closures: (cfg.closures || []).map((c, idx) => idx === i ? { ...c, [field]: val } : c) });
  const delClosure = (i) => setCfg({ ...cfg, closures: (cfg.closures || []).filter((_, idx) => idx !== i) });

  const saveCfg = async () => {
    setBusy(true);
    try {
      await api.put("/booking-config", {
        hours: cfg.hours, closures: (cfg.closures || []).filter((c) => c.from),
        google_review_link: cfg.google_review_link || "",
        reminders_enabled: cfg.reminders_enabled, reviews_enabled: cfg.reviews_enabled,
        reminder_hours: Number(cfg.reminder_hours) || 2,
        blocked_month_days: String(cfg._bmdText ?? (cfg.blocked_month_days || []).join(", "))
          .split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => n >= 1 && n <= 31),
        block_last_day: cfg.block_last_day !== false,
        min_notice_days: Math.max(0, Number(cfg.min_notice_days ?? 3) || 0),
        sms_confirm_text: cfg.sms_confirm_text || "",
        sms_reminder_text: cfg.sms_reminder_text || "",
        sms_review_text: cfg.sms_review_text || "",
      });
      setNotice("Impostazioni calendario salvate.");
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  if (!cfg) return <p className="muted">Caricamento…</p>;

  return (
    <div>
      {notice && <p className="muted" style={{ color: "#7dd3fc" }} data-testid="cal-settings-notice">{notice}</p>}

      {/* Calendari collegati */}
      <div className="panel">
        <h3>Calendari sincronizzati</h3>
        <table>
          <thead><tr><th>Calendario</th><th>Stato</th><th>Calendari trovati</th></tr></thead>
          <tbody>
            <tr>
              <td><strong>Google Calendar</strong></td>
              <td><span className={"pill " + (gcal.connected ? "gestita" : "da_gestire")}>{gcal.connected ? "collegato" : "non collegato"}</span></td>
              <td className="muted" data-testid="gcal-calendars-list">{gcal.connected ? (
                Object.entries(gcal.calendars.reduce((acc, c) => {
                  const k = c.account || "account Google";
                  (acc[k] = acc[k] || []).push(c.name);
                  return acc;
                }, {})).map(([account, names]) => (
                  <div key={account}><strong>{account}</strong>: {names.join(", ")}</div>
                ))
              ) : <a href="/impostazioni?tab=email">Collega da Caselle email</a>}</td>
            </tr>
            <tr>
              <td><strong>iCloud (Apple)</strong></td>
              <td><span className={"pill " + (icStatus.connected ? "gestita" : "da_gestire")} data-testid="icloud-settings-status">{icStatus.connected ? "collegato" : "non collegato"}</span></td>
              <td className="muted">{icStatus.connected ? `${icStatus.apple_id} · ${(icStatus.calendars || []).map((c) => c.name).join(", ")}` : "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* iCloud connessione */}
      <div className="panel" style={{ marginTop: 16 }}>
        <h3>Collega iCloud Calendar</h3>
        <p className="muted">Inserisci il tuo Apple ID e una <strong>password specifica per app</strong> (la generi su appleid.apple.com → Accesso e sicurezza → Password per app). La password è salvata cifrata.</p>
        {icStatus.connected ? (
          <div className="toolbar">
            <span className="pill gestita">Collegato come {icStatus.apple_id}</span>
            <button className="btn danger sm" onClick={disconnectIc} data-testid="icloud-disconnect-btn">Scollega</button>
          </div>
        ) : (
          <>
            <div className="grid-2">
              <div className="field"><label>Apple ID (email)</label><input value={ic.apple_id} onChange={(e) => setIc({ ...ic, apple_id: e.target.value })} placeholder="nome@icloud.com" data-testid="icloud-apple-id" /></div>
              <div className="field"><label>Password specifica per app</label><input type="password" value={ic.password} onChange={(e) => setIc({ ...ic, password: e.target.value })} placeholder="xxxx-xxxx-xxxx-xxxx" data-testid="icloud-password" /></div>
            </div>
            <button className="btn primary" onClick={connectIc} disabled={busy} data-testid="icloud-connect-btn">{busy ? "Collegamento…" : "Collega iCloud"}</button>
          </>
        )}
      </div>

      {/* Orari prenotabili */}
      <div className="panel" style={{ marginTop: 16 }}>
        <h3>Orari prenotabili dai clienti</h3>
        <p className="muted">Per ogni giorno indica le fasce orarie in cui i clienti possono prenotare. Lascia vuoto un giorno per renderlo non prenotabile.</p>
        {DAYS.map((d) => (
          <div key={d.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
            <div style={{ width: 110, fontWeight: 600, paddingTop: 6 }}>{d.label}</div>
            <div style={{ flex: 1 }}>
              {(cfg.hours?.[d.id] || []).length === 0 && <span className="muted" style={{ display: "inline-block", paddingTop: 6 }}>Chiuso</span>}
              {(cfg.hours?.[d.id] || []).map((s, i) => (
                <div key={i} className="toolbar" style={{ gap: 6, marginBottom: 6 }} data-testid={`slot-${d.id}-${i}`}>
                  <input type="time" style={{ maxWidth: 120 }} value={s.from} onChange={(e) => updSlot(d.id, i, "from", e.target.value)} />
                  <span className="muted">→</span>
                  <input type="time" style={{ maxWidth: 120 }} value={s.to} onChange={(e) => updSlot(d.id, i, "to", e.target.value)} />
                  <button className="btn sm danger" onClick={() => delSlot(d.id, i)}>Rimuovi</button>
                </div>
              ))}
              <button className="btn sm" onClick={() => addSlot(d.id)} data-testid={`add-slot-${d.id}`}>+ Aggiungi fascia</button>
            </div>
          </div>
        ))}
      </div>

      {/* Giornate di chiusura */}
      <div className="panel" style={{ marginTop: 16 }}>
        <h3>Giornate di chiusura</h3>
        <p className="muted">Periodi (ferie, festività, corsi) in cui i clienti non possono prenotare. Per un solo giorno indica la stessa data in entrambi i campi.</p>
        <table>
          <thead><tr><th>Dal</th><th>Al</th><th>Motivo</th><th></th></tr></thead>
          <tbody>
            {(cfg.closures || []).map((c, i) => (
              <tr key={i} data-testid={`closure-${i}`}>
                <td><DateIT value={c.from || ""} onChange={(v) => updClosure(i, "from", v)} /></td>
                <td><DateIT value={c.to || ""} onChange={(v) => updClosure(i, "to", v)} /></td>
                <td><input value={c.reason || ""} onChange={(e) => updClosure(i, "reason", e.target.value)} placeholder="es. Ferie estive" /></td>
                <td><button className="btn sm danger" onClick={() => delClosure(i)}>Elimina</button></td>
              </tr>
            ))}
            {(cfg.closures || []).length === 0 && <tr><td colSpan={4}><div className="empty">Nessuna chiusura impostata.</div></td></tr>}
          </tbody>
        </table>
        <button className="btn sm" onClick={addClosure} style={{ marginTop: 8 }} data-testid="add-closure-btn">+ Aggiungi chiusura</button>
        <div style={{ borderTop: "1px solid var(--border)", marginTop: 14, paddingTop: 12 }}>
          <h4 style={{ margin: "0 0 6px" }}>Giorni del mese mai prenotabili</h4>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>Giorni dedicati alle scadenze fiscali, esclusi dalle prenotazioni online ogni mese.</p>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div className="field" style={{ margin: 0, width: 220 }}>
              <label>Giorni (separati da virgola)</label>
              <input value={cfg._bmdText ?? (cfg.blocked_month_days || [5, 13, 16]).join(", ")}
                onChange={(e) => setCfg({ ...cfg, _bmdText: e.target.value })}
                placeholder="5, 13, 16" data-testid="blocked-month-days" />
            </div>
            <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 14 }}>
              <input type="checkbox" checked={cfg.block_last_day !== false} onChange={(e) => setCfg({ ...cfg, block_last_day: e.target.checked })} data-testid="block-last-day" />
              Blocca anche l'ultimo giorno del mese
            </label>
            <div className="field" style={{ margin: 0, width: 220 }}>
              <label>Preavviso minimo (giorni)</label>
              <input type="number" min="0" max="30" value={cfg.min_notice_days ?? 3}
                onChange={(e) => setCfg({ ...cfg, min_notice_days: e.target.value })} data-testid="min-notice-days" />
              <span className="muted" style={{ fontSize: 12 }}>Dal link pubblico non si può prenotare prima di questo numero di giorni.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Prenotazioni & recensioni */}
      <div className="panel" style={{ marginTop: 16 }}>
        <h3>Promemoria e recensioni</h3>
        <div className="field"><label>Link recensione Google (g.page/r/…)</label>
          <input value={cfg.google_review_link || ""} onChange={(e) => setCfg({ ...cfg, google_review_link: e.target.value })} placeholder="https://g.page/r/..." data-testid="google-review-link" />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <input type="checkbox" checked={!!cfg.reminders_enabled} onChange={(e) => setCfg({ ...cfg, reminders_enabled: e.target.checked })} data-testid="reminders-enabled" />
          <span>Invia promemoria automatici via SMS (conferma il giorno prima + promemoria prima dell'incontro)</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <input type="checkbox" checked={!!cfg.reviews_enabled} onChange={(e) => setCfg({ ...cfg, reviews_enabled: e.target.checked })} data-testid="reviews-enabled" />
          <span>Richiedi una recensione a fine incontro (solo a chi non l'ha già lasciata)</span>
        </label>

        <div className="field" style={{ marginTop: 12, maxWidth: 260 }}>
          <label>Promemoria: quante ore prima dell'appuntamento</label>
          <input type="number" min="1" max="23" value={cfg.reminder_hours ?? 2} onChange={(e) => setCfg({ ...cfg, reminder_hours: e.target.value })} data-testid="reminder-hours" />
        </div>

        <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>Testi degli SMS. Segnaposto disponibili: <code>{"{nome}"}</code> (nome cliente), <code>{"{data}"}</code> (gg/mm), <code>{"{ora}"}</code>, <code>{"{link}"}</code> (link di conferma nel testo di conferma, link recensione nel testo recensione), <code>{"{modalita}"}</code> (dove avviene l'incontro: in studio con indirizzo, in videochiamata Zoom, telefonico, oppure «veniamo noi presso la vostra sede» per le trasferte). Nel messaggio di conferma e nel promemoria link e modalità vengono aggiunti in fondo automaticamente se non usi i segnaposto.</p>

        <div className="field">
          <label>SMS di conferma (il giorno prima)</label>
          <textarea rows={3} value={cfg.sms_confirm_text || ""} onChange={(e) => setCfg({ ...cfg, sms_confirm_text: e.target.value })} data-testid="sms-confirm-text" />
          <SmsPreview text={cfg.sms_confirm_text} kind="confirm" />
        </div>
        <div className="field">
          <label>SMS promemoria (prima dell'incontro)</label>
          <textarea rows={3} value={cfg.sms_reminder_text || ""} onChange={(e) => setCfg({ ...cfg, sms_reminder_text: e.target.value })} data-testid="sms-reminder-text" />
          <SmsPreview text={cfg.sms_reminder_text} kind="reminder" />
        </div>
        <div className="field">
          <label>SMS richiesta recensione (a fine incontro)</label>
          <textarea rows={3} value={cfg.sms_review_text || ""} onChange={(e) => setCfg({ ...cfg, sms_review_text: e.target.value })} data-testid="sms-review-text" />
          <SmsPreview text={cfg.sms_review_text} kind="review" />
        </div>
      </div>

      {/* Link pubblici */}
      <div className="panel" style={{ marginTop: 16 }}>
        <h3>Link pubblici da condividere</h3>
        <p className="muted">Pagina di prenotazione per i clienti (condividila o mettila sul sito / firma email):</p>
        <div className="field">
          <input readOnly value={`${window.location.origin}/prenota`} onFocus={(e) => e.target.select()} data-testid="public-booking-link" />
        </div>
        <p className="muted" style={{ fontSize: 12 }}>Per chiedere una recensione: <code>{window.location.origin}/recensione?appt=ID_APPUNTAMENTO</code> (il link viene inviato automaticamente a fine incontro se l'opzione recensioni è attiva).</p>
      </div>

      <div style={{ marginTop: 16 }}>
        <button className="btn primary" onClick={saveCfg} disabled={busy} data-testid="save-cal-settings">{busy ? "Salvataggio…" : "Salva impostazioni calendario"}</button>
      </div>
    </div>
  );
}
